import { useState } from 'react';

interface Props {
  onExplain: (word: string) => Promise<{ translation?: string; dutch: string; english: string } | null>;
}

export default function WordExplainer({ onExplain }: Props) {
  const [word, setWord] = useState('');
  const [explanation, setExplanation] = useState<{ translation?: string; dutch: string; english: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const handleExplain = async (lookupWord?: string) => {
    const target = lookupWord || word;
    if (!target.trim()) return;
    setLoading(true);
    setVisible(true);
    const result = await onExplain(target.trim());
    setExplanation(result);
    setLoading(false);
  };

  if (!visible) {
    return (
      <button
        onClick={() => setVisible(true)}
        className="text-sm text-gray-500 hover:text-gray-700 underline"
      >
        💡 Don't know a word? Look it up
      </button>
    );
  }

  return (
    <div className="bg-blue-50 rounded-xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-blue-800">💡 Word Explainer (A2)</span>
        <button onClick={() => { setVisible(false); setExplanation(null); }} className="text-blue-500 hover:text-blue-700 text-sm">
          ✕ Close
        </button>
      </div>

      <div className="flex gap-2">
        <input
          type="text"
          value={word}
          onChange={(e) => setWord(e.target.value)}
          placeholder="Type a word..."
          className="flex-1 border border-blue-200 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-blue-400 focus:border-transparent"
          onKeyDown={(e) => e.key === 'Enter' && handleExplain()}
        />
        <button
          onClick={() => handleExplain()}
          disabled={loading || !word.trim()}
          className="bg-blue-600 text-white px-3 py-1.5 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {loading ? '...' : 'Explain'}
        </button>
      </div>

      {explanation && (
        <div className="space-y-2 text-sm">
          {explanation.translation && (
            <div className="bg-white/60 rounded-lg p-2 text-center">
              <span className="font-semibold text-blue-900">{explanation.translation}</span>
            </div>
          )}
          <div>
            <span className="font-medium text-blue-800">🇳🇱 Nederlands:</span>
            <p className="text-gray-700 mt-0.5">{explanation.dutch}</p>
          </div>
          <div>
            <span className="font-medium text-blue-800">🇬🇧 English:</span>
            <p className="text-gray-700 mt-0.5">{explanation.english}</p>
          </div>
        </div>
      )}
    </div>
  );
}

// Exported for use as a modal trigger from exercises
export function useWordExplainer(explainFn: (word: string) => Promise<{ translation?: string; dutch: string; english: string } | null>) {
  const [modalWord, setModalWord] = useState<string | null>(null);
  const [modalExplanation, setModalExplanation] = useState<{ translation?: string; dutch: string; english: string } | null>(null);
  const [modalLoading, setModalLoading] = useState(false);

  const openExplainer = async (word: string) => {
    setModalWord(word);
    setModalLoading(true);
    const result = await explainFn(word);
    setModalExplanation(result);
    setModalLoading(false);
  };

  const closeExplainer = () => {
    setModalWord(null);
    setModalExplanation(null);
  };

  return { modalWord, modalExplanation, modalLoading, openExplainer, closeExplainer };
}
